import {
  WSEmptyState,
  WSFilters,
  WSInfiniteScroll,
  WSList,
  WSTableColumnSortDirection,
  WSTableItem,
  WSTableToolbar
} from "@wingspanhq/fe-component-library";
import { PayrollWorkflowStrategy } from "@wingspanhq/payments/dist/interfaces";
import { PayableBulkActionType } from "@wingspanhq/wingspan-api-types/dist/src/generated/paymentsApiSpec";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Route } from "react-router-dom";
import { PayableDetails } from "../../../../Invoices/screens/payables/PayableDetails";
import { useUserId } from "../../../../query/hooks/helpers";
import { useQueryPayablesSummary } from "../../../../query/payables/useQueryPayablesSummary";
import { useQueryPayrollSettings } from "../../../../query/payments/queries";
import { useQueryPayablesWithPayees } from "../../../../query/payables/useQueryPayablesWithPayees";
import { WSQueries } from "../../../../query/WSQuery";
import {
  Payable,
  PayablesSortField
} from "../../../../services/api/payments/payables";
import { useUrlQuery } from "../../../../shared/hooks/useUrlQuery";
import { PayablesTabSlug, routeHelpersPayablesDetails } from "../../paths";
import { useBulkApprovePayables } from "../../utils/useBulkApprovePayables";
import { useBulkCancelPayables } from "../../utils/useBulkCancelPayables";
import { useBulkDeletePayables } from "../../utils/useBulkDeletePayables";
import { useBulkOpenPayables } from "../../utils/useBulkOpenPayables";
import { useBulkPreApprovePayables } from "../../utils/useBulkPreApprovePayables";
import { useBulkUnapprovePayables } from "../../utils/useBulkUnapprovePayables";
import { useDownloadPayablesCsv } from "../../utils/useDownloadPayablesCsv";
import { ColumnKey, PayeeDisplayInfo } from "./columns";
import { FeaturedBulkAction } from "./FeaturedBulkAction";
import { useBuildConfig } from "./filters/config";
import { createListRequest } from "./filters/request";
import {
  Filter,
  PayablesTabFilter,
  PayablesVisibleFilters,
  SortField
} from "./filters/types";
import { TablePayables } from "./TablePayables";

export type PayableBulkActionKey =
  | "approve"
  | "preapprove"
  | "unapprove"
  | "delete"
  | "open"
  | "cancel";

export type EmptyStateCopy = {
  firstTime?: string;
  standart: string;
};

type Props = {
  tab: PayablesTabFilter;
  visibleColumns?: Partial<Record<ColumnKey, boolean>>;
  visibleFilters?: PayablesVisibleFilters;
  payeesMap?: Map<string, PayeeDisplayInfo>;
  showDownloadCsv?: boolean;
  emptyState?: EmptyStateCopy;
  bulkActions?: PayableBulkActionKey[];
  featuredBulkAction?: PayableBulkActionType;
};

const SORT_FIELD_TO_API: Record<SortField, PayablesSortField> = {
  createdAt: "createdAt",
  updatedAt: "updatedAt",
  "events.paidAt": "events.paidAt"
};

const BULK_ACTION_LABELS: Record<PayableBulkActionKey, string> = {
  approve: "Approve for payment",
  preapprove: "Pre-approve",
  unapprove: "Unapprove",
  delete: "Delete",
  open: "Move to open",
  cancel: "Cancel"
};

export const FilterableTablePayables: React.FC<Props> = props => {
  const userId = useUserId();
  const { urlQuery, setUrlQuery } = useUrlQuery<Filter, SortField>();

  const payrollSettingsQuery = useQueryPayrollSettings(userId, {
    retry: false
  });
  const isDualStage =
    payrollSettingsQuery.data?.workflow === PayrollWorkflowStrategy.DualStage;

  const goToPayableDetails = routeHelpersPayablesDetails.useGoTo();

  const handleRowClick = useCallback(
    (payable: Payable) => {
      if (payable.payableId) {
        goToPayableDetails({
          tab: props.tab as PayablesTabSlug,
          payableId: payable.payableId
        });
      }
    },
    [goToPayableDetails, props.tab]
  );

  const [selection, setSelection] = useState<WSTableItem<Payable>[]>([]);
  const clearSelection = useCallback(() => setSelection([]), []);

  // Clear selection whenever the tab or filters/sort change to avoid acting
  // on stale rows that may no longer be visible.
  useEffect(() => {
    setSelection([]);
  }, [props.tab, urlQuery.filter, urlQuery.sort]);

  const bulkApprove = useBulkApprovePayables();
  const bulkPreApprove = useBulkPreApprovePayables();
  const bulkUnapprove = useBulkUnapprovePayables();
  const bulkDelete = useBulkDeletePayables();
  const bulkOpen = useBulkOpenPayables();
  const bulkCancel = useBulkCancelPayables();

  const bulkActionHandlers = useMemo<
    Record<PayableBulkActionKey, (payables: Payable[]) => void>
  >(
    () => ({
      approve: payables => bulkApprove(payables, clearSelection),
      preapprove: payables => bulkPreApprove(payables, clearSelection),
      unapprove: payables => bulkUnapprove(payables, clearSelection),
      delete: payables => bulkDelete(payables, clearSelection),
      open: payables => bulkOpen(payables, clearSelection),
      cancel: payables => bulkCancel(payables, clearSelection)
    }),
    [
      bulkApprove,
      bulkPreApprove,
      bulkUnapprove,
      bulkDelete,
      bulkOpen,
      bulkCancel,
      clearSelection
    ]
  );

  const bulkActionKeys = props.bulkActions;
  const hasBulkActions = !!bulkActionKeys && bulkActionKeys.length > 0;

  const request = useMemo(
    () =>
      createListRequest(
        userId,
        urlQuery.filter,
        urlQuery.sort,
        props.tab,
        isDualStage
      ),
    [userId, urlQuery.filter, urlQuery.sort, props.tab, isDualStage]
  );

  // Tab-only request (no user filters / no sort) — used to detect the
  // "first time" empty state: zero results even when the user hasn't narrowed
  // anything down. Mirrors legacy PayablesEmptyState behavior.
  const unfilteredTabRequest = useMemo(
    () =>
      createListRequest(userId, undefined, undefined, props.tab, isDualStage),
    [userId, props.tab, isDualStage]
  );

  const filtersConfig = useBuildConfig(props.visibleFilters);
  const queryPayables = useQueryPayablesWithPayees(request);
  const queryPayablesSummary = useQueryPayablesSummary(request);

  const hasUserFilters = useMemo(
    () => Object.keys(urlQuery.filter ?? {}).length > 0,
    [urlQuery.filter]
  );

  // Only run the unfiltered summary when we need to disambiguate "first time"
  // vs "filters narrowed to zero" — i.e. when there are no user filters
  // applied. When filters are present, we know the empty state is the
  // "standart" copy regardless.
  const queryUnfilteredSummary = useQueryPayablesSummary(unfilteredTabRequest, {
    enabled: !hasUserFilters && props.emptyState !== undefined
  });

  const payeesMap = props.payeesMap ?? queryPayables.payeesMap;

  const downloadCsv = useDownloadPayablesCsv({
    request,
    resultSetId: queryPayables.resultSetId
  });

  const canDownloadCsv = !!props.showDownloadCsv && !downloadCsv.isDisabled;

  const handleSortChange = useCallback(
    (field: PayablesSortField, direction: WSTableColumnSortDirection) => {
      setUrlQuery(prev => ({
        ...prev,
        sort: { field: field as SortField, direction }
      }));
    },
    [setUrlQuery]
  );

  const sortFieldForTable = urlQuery.sort
    ? SORT_FIELD_TO_API[urlQuery.sort.field]
    : undefined;

  const renderEmptyState = (emptyState: EmptyStateCopy) => {
    // "First time" = no filters applied AND the tab itself has zero payables.
    const hasZeroEver = (queryUnfilteredSummary.data?.listSize ?? 0) === 0;
    const showFirstTime =
      !hasUserFilters && hasZeroEver && !!emptyState.firstTime;

    const description = showFirstTime
      ? emptyState.firstTime
      : emptyState.standart;

    return (
      <WSEmptyState
        py="3XL"
        hasBorder
        bgColor="gray"
        type={hasUserFilters ? "search" : "inbox"}
        title={hasUserFilters ? "No results found" : "No payables"}
        description={description}
      />
    );
  };

  return (
    <WSList gap="2XL">
      <Route
        path={routeHelpersPayablesDetails.path}
        component={PayableDetails}
      />

      {props.featuredBulkAction && (
        <FeaturedBulkAction
          action={props.featuredBulkAction}
          summary={queryPayablesSummary.data}
          resultSetId={queryPayables.resultSetId}
        />
      )}

      <WSFilters<Filter>
        values={urlQuery.filter}
        onFilter={newValue => {
          setUrlQuery({ filter: newValue });
        }}
        primaryFilters={filtersConfig}
        info={{
          count: queryPayablesSummary.data?.listSize ?? 0,
          amount: queryPayablesSummary.data?.totalValue
        }}
      />

      {bulkActionKeys && bulkActionKeys.length > 0 && selection.length > 0 && (
        <WSTableToolbar
          count={selection.length}
          onClear={clearSelection}
          actionsLabel="Bulk actions"
          actions={bulkActionKeys.map(key => ({
            label: BULK_ACTION_LABELS[key],
            destructive: key === "delete" || key === "cancel",
            onClick: () =>
              bulkActionHandlers[key](selection.map(item => item.data))
          }))}
        />
      )}

      <WSQueries queries={{ queryPayables }}>
        {({ queryPayablesData }) =>
          queryPayablesData.length === 0 && props.emptyState ? (
            renderEmptyState(props.emptyState)
          ) : (
            <WSInfiniteScroll
              mt="M"
              onLoad={() => queryPayables.fetchNextPage()}
              loadMore={queryPayablesData.length > 0}
              endOfList={!queryPayables.hasNextPage}
              loading={!!queryPayables.isFetchingNextPage}
            >
              <TablePayables
                payables={queryPayablesData}
                onRowClick={handleRowClick}
                onSortChange={handleSortChange}
                sortField={sortFieldForTable}
                sortDirection={urlQuery.sort?.direction}
                visibleColumns={props.visibleColumns}
                payeesMap={payeesMap}
                canDownloadCsv={canDownloadCsv}
                isDownloadingCsv={downloadCsv.isLoading}
                onDownloadCsv={downloadCsv.download}
                selection={hasBulkActions ? selection : undefined}
                onSelectionChange={hasBulkActions ? setSelection : undefined}
              />
            </WSInfiniteScroll>
          )
        }
      </WSQueries>
    </WSList>
  );
};
